import Select, { StylesConfig, components } from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import makeAnimated from "react-select/animated";

import React, { ReactNode, forwardRef, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Label from "./fromLabel";
import { DropdownOptionType, Option } from "../../../_config/types";
import Error from "./fromError";
import Icon from "../../icons";
import { useTranslation } from "react-i18next";

// Built once at module scope, not per render. makeAnimated() constructs a fresh
// component set on every call, so calling it inside the component made every
// SelectBox instance rebuild it on every render.
const animatedComponents = makeAnimated();

const sizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-md",
};

// Stable reference so the styles object below doesn't change identity per render.
const menuPortalStyle = (base: any) => ({ ...base, zIndex: 9999 });

// type DropDownProps<OptionType> = AsyncProps<OptionType, false>;
interface props {
  async?: boolean;
  label?: string;
  required?: boolean;
  placeholder?: string;
  value?: DropdownOptionType;
  isClearable?: boolean;
  onChange?: (data: any) => void;
  options?: Option[];
  isMulti?: boolean;
  defaultValue?: Option | null;
  error?: string;
  disabled?: boolean;
  info?: string;
  asyncLoadOptions?: (value: string) => Promise<any[]>;
  asyncFormatOptionLabel?: (option: any) => ReactNode;
  customStyleOption?: StylesConfig;
  className?: string;
  classNamePrefix?: "arrow" | "arrow-sm";
  size?: "sm" | "md" | "lg";
  menuPlacement?: "auto" | "bottom" | "top";
  topParentStyle?: React.CSSProperties; // This prop is not used in the component, but can be used to pass styles to the top parent div if needed
  onDocument?: boolean;
  labelisElipsis?: boolean;
  topClassName?: string;
  mtx?: boolean; // margin top x
  hideErrorMessage?: boolean;
  isSearchable?: boolean;
  customComponents?: any;
  menuShouldScrollIntoView?: boolean;
  enableSelectAll?: boolean; // Enable "Select All" option for multi-select
}

const SelectBox = forwardRef<any, props>(
  (
    {
      async = false,
      label,
      required = false,
      placeholder: placeholderProp,
      options = [],
      isClearable = false,
      value = [],
      onChange = () => {},
      isMulti = false,
      defaultValue = null,
      error,
      disabled = false,
      info,
      asyncLoadOptions,
      asyncFormatOptionLabel,
      customStyleOption = {},
      className = "",
      classNamePrefix = "arrow",
      size = "md",
      menuPlacement = "auto",
      topParentStyle = {}, // This prop is not used in the component, but can be used to pass styles to the top parent div if needed
      onDocument = false,
      labelisElipsis = false,
      topClassName = "",
      mtx = false,
      hideErrorMessage = false,
      isSearchable = true,
      customComponents,
      menuShouldScrollIntoView = true,
      enableSelectAll = true,
    },
    ref,
  ) => {
    const { t } = useTranslation("common");
    const placeholder = placeholderProp ?? t("selectBox.select");
    // For multi-select, use custom portal with checkboxes
    const [showOptions, setShowOptions] = React.useState(false);
    const optionsContainerRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = React.useState<{
      top: number;
      left: number;
      width: number;
      placement: "top" | "bottom";
    }>({
      top: 0,
      left: 0,
      width: 0,
      placement: "bottom",
    });

    // 1️⃣ Add search state
    const [search, setSearch] = useState("");
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Auto-focus search input when dropdown opens
    React.useEffect(() => {
      if (showOptions && searchInputRef.current && isSearchable) {
        searchInputRef.current.focus();
      }
    }, [showOptions, isSearchable]);

    // 2️⃣ Filter options based on search
    const filteredOptions = useMemo(() => {
      if (!Array.isArray(options)) return [];
      const term = search.toLowerCase();
      return options.filter(
        (opt) =>
          opt &&
          typeof opt === "object" &&
          typeof opt.label === "string" &&
          opt.label.toLowerCase().includes(term)
      );
    }, [options, search]);

    // Handle checkbox change
    const handleCheckboxChange = (checked: boolean, opt: Option) => {
      if (!opt) return;
      let newValue: Option[] = Array.isArray(value) ? [...value] : [];
      if (checked) {
        if (!newValue.some((v: any) => String(v.value) === String(opt.value))) {
          newValue.push(opt);
        }
      } else {
        newValue = newValue.filter(
          (v: any) => String(v.value) !== String(opt.value),
        );
      }
      onChange(newValue);
    };

    const calcMenuPosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 220;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? "top" : "bottom";
      setMenuPosition({
        left: rect.left + window.scrollX,
        width: rect.width,
        placement,
        top: placement === "bottom" ? rect.bottom + window.scrollY : rect.top + window.scrollY,
      });
    };

    // Recalculate after mount in case layout shifted (useLayoutEffect = before paint)
    React.useLayoutEffect(() => {
      if (!showOptions) return;
      calcMenuPosition();
    }, [showOptions]);

    // Close portal on outside click or scroll
    React.useEffect(() => {
      if (!showOptions) return;
      const handleClick = (e: MouseEvent) => {
        if (
          optionsContainerRef.current &&
          !optionsContainerRef.current.contains(e.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target as Node)
        ) {
          setShowOptions(false);
        }
      };
      // Only close on scroll events that are not from the dropdown menu itself
      const handleScroll = (e: Event) => {
        if (
          optionsContainerRef.current &&
          e.target &&
          optionsContainerRef.current.contains(e.target as Node)
        ) {
          // Ignore scrolls inside the dropdown
          return;
        }
        setShowOptions(false);
      };
      document.addEventListener("mousedown", handleClick);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("mousedown", handleClick);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }, [showOptions]);

    // For single-select, keep react-select logic
    const optionsWithSelectAll = useMemo(() => {
      if (!isMulti || !enableSelectAll) return options;
      return [{ value: "__select_all__", label: t("selectBox.selectAll") }, ...options];
    }, [isMulti, enableSelectAll, options, t]);

    const handleChange = (selectedOptions: any) => {
      if (!isMulti || !enableSelectAll) {
        onChange(selectedOptions);
        return;
      }
      // ...existing code for select all logic...
    };

    // A stored selection keeps the label it had when it was picked, so after a
    // language switch it would stay in the old language while the options are
    // already retranslated. Re-resolve each selected option against the current
    // options by value; anything without a match is passed through untouched.
    const valueWithSelectAll = useMemo(() => {
      if (!Array.isArray(options) || options.length === 0 || !value) return value;
      const hasSelection = Array.isArray(value) ? value.length > 0 : true;
      if (!hasSelection) return value;
      // Only primitive values are safe to match on; String() of an object value
      // is "[object Object]" for every option. Booleans get their own key space so
      // true never collides with the string "true".
      const keyOf = (x: any): string | null => {
        if (typeof x === "string") return x === "" ? null : x;
        if (typeof x === "number") return String(x);
        if (typeof x === "boolean") return `__bool:${x}`;
        return null;
      };
      // First option wins for a repeated value, matching the previous Array.find.
      const byValue = new Map<string, any>();
      options.forEach((o: any) => {
        const k = o && typeof o === "object" ? keyOf(o.value) : null;
        if (k !== null && !byValue.has(k)) byValue.set(k, o);
      });
      const refresh = (v: any) => {
        if (!v || typeof v !== "object") return v;
        const k = keyOf(v.value);
        const match = k === null ? undefined : byValue.get(k);
        // Never swap in an option that has no label of its own.
        if (!match || match.label == null || match.label === "" || match.label === v.label) return v;
        return match;
      };
      return Array.isArray(value) ? value.map(refresh) : refresh(value);
    }, [value, options]);

    // Memoized so react-select / emotion don't re-serialize styles on every render.
    const defaultStyles = useMemo(() => ({
      control: (base: any, state: any) => ({
        ...base,
        borderStyle: "solid",
        borderRadius: "0.375rem",
        borderColor: error
          ? "#dc3545 !important"
          : state.isFocused
            ? "#2d72d2"
            : "#dee2e6",
        borderWidth: error ? "2px" : "1px",
        minHeight: size == "sm" ? "2rem" : "2.875rem",
        boxShadow: "none",
        padding: "0 0",
        //marginTop:size == 'sm'?0: '0.5rem',
        marginTop: mtx || size == "sm" ? 0 : "0.5rem",
        backgroundColor: disabled ? "#f9f9f9" : "white",
        "&:hover": {
          borderColor: error ? "#dc3545" : "#CECECE",
        },
      }),
      valueContainer: (base: any) => ({
        ...base,
        padding: "0 12px",
        maxHeight: "120px",
        overflowY: "auto",
      }),
      dropdownIndicator: (base: any) => ({
        ...base,
        padding: 0,
        paddingRight: "8px",
      }),
      clearIndicator: (base: any) => ({
        ...base,
        padding: 0,
        paddingRight: "8px",
      }),
      singleValue: (base: any) => ({
        ...base,
        whiteSpace: "normal",
        wordWrap: "break-word",
      }),
      menu: (base: any) => ({
        ...base,
        zIndex: 30,
        // zIndex: 20,
        boxShadow: "0 0 0 1px rgba(0,0,0,.1), 0 4px 11px rgba(0,0,0,.1)",
      }),
      menuList: (base: any) => ({
        ...base,
        maxHeight: "220px",
      }),
      option: (base: any, state: any) => {
        const isSelectAll = state.data?.value === "__select_all__";
        return {
          ...base,
          backgroundColor: state.isSelected
            ? "#e9f1ff"
            : state.isFocused
              ? "#f2f9ff"
              : "white",
          color: isSelectAll
            ? "#28B4ED"
            : state.isSelected
              ? "#175ddc"
              : "#333",
          fontWeight: "normal",
          "&:active": {
            backgroundColor: "#e9f1ff",
          },
        };
      },
      placeholder: (base: any) => ({
        ...base,
        color: "#6c757d",
      }),
      indicatorSeparator: (base: any) => ({
        ...base,
        display: "none",
      }),
    }), [error, size, mtx, disabled]);

    // Merged style + component objects, memoized so the props react-select receives
    // keep a stable identity between renders.
    const mergedStyles = useMemo(
      () => ({
        ...defaultStyles,
        ...customStyleOption,
        menuPortal: menuPortalStyle,
      }) as StylesConfig,
      [defaultStyles, customStyleOption]
    );

    const mergedComponents = useMemo(
      () => ({ ...animatedComponents, ...customComponents }),
      [customComponents]
    );

    const classes = `${sizeClasses[size]} font-medium custom-select ${
      classNamePrefix === "arrow"
        ? "react-select-arrow"
        : "react-select-arrow-sm"
    } ${error ? "has-error " : ""}${className}`;

    return (
      <div
        className={`mb-3 ${topClassName}`}
        style={{ ...topParentStyle, position: "relative" }}
      >
        {label && (
          <Label text={label} isElipsis={labelisElipsis} required={required} />
        )}

        {isMulti ? (
          <div className={classes} style={{ position: "relative" }}>
            <div
              ref={triggerRef}
              className={`w-full min-h-[2.875rem] px-3 py-2 border ${error ? "border-[#dc3545]" : "border-[#cecece]"} rounded-md focus:outline-none ${disabled ? "cursor-not-allowed" : "cursor-pointer"} mt-2 relative flex items-center`}
              style={{
                fontSize: size === "sm" ? 13 : 15,
                fontFamily: "inherit",
                marginTop: mtx || size === "sm" ? 0 : "0.5rem",
                outline: showOptions ? "2px solid #2d72d2" : "none",
                outlineOffset: 0,
                boxShadow: "none",
                minHeight: size === "sm" ? "2rem" : "2.875rem",
                backgroundColor: disabled ? "#f9f9f9" : "white",
                opacity: disabled ? 0.6 : 1,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                if (!disabled) {
                  calcMenuPosition();
                  setShowOptions(true);
                }
              }}
            >
              {Array.isArray(valueWithSelectAll) && valueWithSelectAll.length > 0 ? (
                <>
                  <div className="flex w-full items-center gap-2">
                    <div
                      className="flex-1 min-w-0 flex flex-wrap gap-1 overflow-y-auto transition-[max-height] duration-300 ease-in-out"
                      style={{ maxHeight: 125 }}
                    >
                      {Array.isArray(valueWithSelectAll)
                        ? valueWithSelectAll.map((v: any, idx: number) => {
                            if (!v || typeof v !== "object" || !('label' in v) || !('value' in v)) return null;
                            return (
                              <div
                                key={String(v.value) || idx}
                                className="bg-[#e5e7eb] rounded-sm flex items-center gap-2 text-xs text-black w-full px-2 py-2"
                                style={{
                                  border: "1px solid #d1d5db",
                                  fontWeight: 500,
                                }}
                                title={v.label ?? ''}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span className="flex-1 min-w-0 break-words">
                                  {v.label ?? ''}
                                </span>
                                <button
                                  type="button"
                                  role="button"
                                  className="disabled:cursor-not-allowed disabled:opacity-50 shrink-0 w-4 h-4 flex items-center justify-center hover:bg-[#f5aa9d] hover:text-[#f53f1f] rounded cursor-pointer"
                                  aria-label={`Remove ${v.label ?? ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (disabled) return;
                                    const newValue = Array.isArray(value)
                                      ? value.filter(
                                          (item: any) =>
                                            item && String(item.value) !== String(v.value),
                                        )
                                      : [];
                                    onChange(newValue);
                                  }}
                                  disabled={disabled}
                                >
                                  <Icon name="close" size={12} />
                                </button>
                              </div>
                            );
                          })
                        : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 self-center bg-white pl-1" style={{ pointerEvents: "auto" }}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!disabled) onChange([]);
                        }}
                        className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Icon name="close" size={16} />
                      </button>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between w-full h-full text-[#6c757d] text-sm">
                  <span className="font-semibold">
                    {placeholder}
                  </span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              )}
            </div>
            {showOptions && typeof window !== "undefined" && (
              <div
                ref={optionsContainerRef}
                style={{
                  position: onDocument ? "fixed" : "absolute",
                  left: onDocument ? menuPosition.left : 0,
                  width: onDocument ? menuPosition.width : "100%",
                  maxHeight: 260,
                  background: "#fff",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  boxShadow:
                    "0 0 0 1px rgba(0,0,0,.08), 0 6px 18px rgba(0,0,0,.12)",
                  zIndex: 9999,
                  // ✅ AUTO PLACEMENT
                  top: onDocument
                    ? menuPosition.placement === "bottom"
                      ? menuPosition.top
                      : "auto"
                    : menuPosition.placement === "bottom"
                      ? "100%"
                      : "auto",
                  bottom: onDocument
                    ? menuPosition.placement === "top"
                      ? window.innerHeight - menuPosition.top
                      : "auto"
                    : menuPosition.placement === "top"
                      ? "100%"
                      : "auto",
                  marginTop: menuPosition.placement === "bottom" ? 4 : 0,
                  marginBottom: menuPosition.placement === "top" ? 4 : 0,
                  transform:
                    menuPosition.placement === "top"
                      ? "translateY(-2px)"
                      : "translateY(2px)",
                  display: "flex",
                  flexDirection: "column",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* 🔍 Search INSIDE dropdown */}
                {isSearchable && (
                  <div style={{ padding: "8px" }}>
                    <div style={{ position: "relative", width: "100%" }}>
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t("selectBox.searchPlaceholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 32px 8px 10px", // right padding for icon
                          borderRadius: 4,
                          border: "1px solid #ced4da",
                          outline: "none",
                          fontSize: 14,
                        }}
                      />
                      <span style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center"
                      }}>
                        <Icon name="search" size={16} />
                      </span>
                    </div>
                  </div>
                )}

                {/* Options list */}
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: "auto",
                    padding: "4px 8px",
                    overscrollBehavior: "contain",
                  }}
                >
                  {enableSelectAll && filteredOptions.length > 0 && filteredOptions.length === options.length && (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 4px",
                        cursor: "pointer",
                        color: "#28B4ED",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(value) &&
                          value.length === filteredOptions.length
                        }
                        disabled={disabled}
                        onChange={(e) =>
                          onChange(e.target.checked ? filteredOptions : [])
                        }
                      />
                      {t("selectBox.selectAll")}
                    </label>
                  )}

                  {filteredOptions.length === 0 && (
                    <div style={{ padding: 8, color: "#999", fontSize: 13 }}>
                      {t("selectBox.noResultsFound")}
                    </div>
                  )}

                  {filteredOptions.map((opt, idx) => {
                    if (!opt || typeof opt !== "object" || !('label' in opt) || !('value' in opt)) return null;
                    const isChecked =
                      Array.isArray(value) &&
                      value.some(
                        (v: any) => v && String(v.value) === String(opt.value)
                      );

                    return (
                      <label
                        key={String(opt.value) || idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 4px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={disabled}
                          onChange={(e) =>
                            handleCheckboxChange(e.target.checked, opt)
                          }
                          style={{ marginTop: 2, flexShrink: 0 }}
                        />

                        <span
                          style={{
                            flex: 1,
                            whiteSpace: "pre-line",
                            wordBreak: "break-word",
                            fontSize: 14,
                          }}
                        >
                          {opt.label ?? ""}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : async ? (
          <AsyncSelect
            ref={ref}
            cacheOptions
            isMulti={isMulti}
            className={classes}
            classNamePrefix={classNamePrefix}
            defaultValue={defaultValue}
            value={valueWithSelectAll as any}
            loadOptions={asyncLoadOptions}
            defaultOptions
            components={mergedComponents}
            formatOptionLabel={asyncFormatOptionLabel}
            onChange={handleChange}
            isDisabled={disabled}
            isClearable={isClearable}
            isSearchable={isSearchable}
            placeholder={placeholder}
            styles={mergedStyles}
            menuPortalTarget={onDocument ? document.body : undefined}
          />
        ) : (
          <Select
            ref={ref}
            components={mergedComponents}
            isMulti={isMulti}
            defaultValue={defaultValue}
            className={classes}
            classNamePrefix={classNamePrefix}
            value={valueWithSelectAll as any}
            onChange={handleChange}
            isDisabled={disabled}
            isClearable={isClearable}
            isSearchable={isSearchable}
            options={optionsWithSelectAll}
            placeholder={placeholder}
            menuPlacement={menuPlacement}
            styles={mergedStyles}
            menuShouldScrollIntoView={menuShouldScrollIntoView}
            menuPortalTarget={onDocument ? document.body : undefined}
          />
        )}

        {error && !hideErrorMessage && <Error message={error} />}
      </div>
    );
  },
);

export default SelectBox;
